import React, { useState, useEffect } from 'react';
import SelectField from './SelectField';
import { FaTimes, FaSave } from 'react-icons/fa';
import Modal from './Modal';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `loginOptions`: usernames of Call Agent logins not yet tied to any calls, which can be linked here.
const AgentEmailModal = ({ open, onClose, onSubmit, agent, gmUsername = [], loginOptions = [] }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [selectedGM, setSelectedGM] = useState('');
  const [login, setLogin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A login is linked to the agent named on the calls. An agent that IS a login (made for a user with
  // no calls yet) has nothing to link, so the field is shown only for agents known from their calls.
  const canLinkLogin = !!agent && !agent.loginOnly;
  // Matched by exact name (no explicit link): the login already works, and is shown as such.
  const matchedByName = !!agent && !agent.linkedLogin && !!agent.loginUsername;

  useEffect(() => {
    if (!open) return;
    setEmail(agent?.email || '');
    setRole(agent?.role || ''); // Reset or set initial role if available
    setSelectedGM(agent?.attachedGM || agent?.AttachedGM || '');
    setLogin(agent?.linkedLogin || '');
  }, [open, agent]);

  // Validation logic
  const emailValid = !email || emailRegex.test(email);
  // Anyone except a GM can report to a General Manager (2026-10-20); it used to
  // be Junior Managers only. A JM still has to have one.
  const isJuniorManager = role === 'JM';
  const isGeneralManager = role === 'GM' || role === 'General Manager'; // older rows say the latter
  const canReportToGM = !isGeneralManager;
  const roleValid = !isJuniorManager || (isJuniorManager && !!selectedGM);
  const gmChoices = gmUsername.filter((gm) => gm.name !== agent?.name);
  
  const canSubmit = emailValid && roleValid && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      // The link is sent only when it changed, so saving an email or role never touches it.
      const loginChange = canLinkLogin && login !== (agent.linkedLogin || '') ? login : undefined;
      await onSubmit(agent.name, email || null, role || null, canReportToGM ? selectedGM || null : null, loginChange);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Set Details for ${agent?.name || 'Agent'}`}>
      <div className="space-y-4">
        {/* Agent Name Field */}
        <div>
          <label htmlFor="agent-name" className="block text-sm font-semibold text-gray-800 mb-1.5">
            Agent Name
          </label>
          <input
            id="agent-name"
            className="input-field bg-gray-100"
            type="text"
            value={agent?.name || ''}
            disabled
          />
        </div>

        {/* Login link: for when a user's username differs from the name their calls carry */}
        {canLinkLogin && (
          <div>
            <label htmlFor="agent-login" className="block text-sm font-semibold text-gray-800 mb-1.5">
              Login
            </label>
            {matchedByName ? (
              <p className="text-sm text-gray-700">
                Signs in as <b>{agent.loginUsername}</b>, which matches this agent's name.
              </p>
            ) : (
              <>
                <SelectField
                  id="agent-login"
                  className="input-field bg-white"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                >
                  <option value="">No login linked</option>
                  {[...new Set([agent?.linkedLogin, ...loginOptions].filter(Boolean))].map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </SelectField>
                <p className="mt-1 text-sm text-gray-500">
                  Link the Call Agent user who handles these calls, when their username differs from the
                  name on the calls. They then see these calls under My Calls and can dispute them.
                </p>
              </>
            )}
          </div>
        )}

        {/* Email Field */}
        <div>
          <label htmlFor="agent-email" className="block text-sm font-semibold text-gray-800 mb-1.5">
            Email Address
          </label>
          <input
            id="agent-email"
            className="input-field"
            type="email"
            placeholder="agent@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!emailValid && email && (
            <p className="mt-1 text-sm text-red-600" aria-live="polite">
              Please enter a valid email address.
            </p>
          )}
          {/* The login email is offered, not filled in, so saving a role never changes the email as a
              side effect. */}
          {agent?.loginEmail && email !== agent.loginEmail && (
            <p className="mt-1 text-sm text-gray-600">
              Login email: {agent.loginEmail}{' '}
              <button type="button" className="text-cloudza-700 underline" onClick={() => setEmail(agent.loginEmail)}>
                Use it
              </button>
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Leave empty to remove the email address.
          </p>
        </div>

        {/* Role Selection Field */}
        <div>
          <label htmlFor="agent-role" className="block text-sm font-semibold text-gray-800 mb-1.5">
            Role
          </label>
          <SelectField
            id="agent-role"
            className="input-field bg-white"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if (e.target.value === 'GM') setSelectedGM(''); // a GM can't report to a GM
            }}
          >
            <option value="">Select a role (Optional)</option>
            <option value="GM">General Manager</option>
            <option value="JM">Junior Manager</option>
            <option value="QA">QA Assessor</option>
          </SelectField>
        </div>

        {/* GM Selection - anyone but a GM; required for a Junior Manager */}
        {canReportToGM && (
          <div className="animate-fade-in">
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Reports to General Manager {isJuniorManager && <span className="text-red-500">*</span>}
            </label>
            <SelectField
              className={`input-field bg-white ${isJuniorManager && !selectedGM ? 'border-red-300' : ''}`}
              value={selectedGM}
              onChange={(e) => setSelectedGM(e.target.value)}
            >
              <option value="">{isJuniorManager ? '-- Select a GM --' : 'No General Manager'}</option>
              {gmChoices.map((gm) => (
                <option key={gm.name} value={gm.name}>
                  {gm.name} {gm.email ? `(${gm.email})` : ''}
                </option>
              ))}
            </SelectField>
            {gmChoices.length === 0 && (
              <p className={`mt-1 text-xs italic ${isJuniorManager ? 'text-red-500' : 'text-gray-500'}`}>
                No General Managers found.{isJuniorManager ? ' You must create a GM first.' : ''}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-outline" disabled={isSubmitting}>
            <FaTimes />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary disabled:opacity-50"
          >
            <FaSave />
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentEmailModal;